import React from 'react';
import { Box, Card, CardContent, Chip, Grid, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { QuotationFlightInfo } from '../../types';
import moment from 'moment';
import { isoDateToFullDate } from '../../helpers';
import FlightIcon from '@mui/icons-material/Flight';

const airportCss = {
	fontSize: '22px',
	fontStyle: 'normal',
	fontWeight: 800,
	color: '#31194B',
	lineHeight: 1,
};

const hourCss = {
	fontSize: '18px',
	fontStyle: 'normal',
	fontWeight: 700,
	color: '#272727',
};

const useStyles = makeStyles((theme) => ({
	card: {
		marginTop: theme.spacing(1.25),
		width: '100%',
		borderRadius: 10,
		border: '1px solid rgba(49, 25, 75, 0.10)',
		boxShadow: '0 6px 18px rgba(49, 25, 75, 0.08)',
		overflow: 'hidden',
	},
	airlineName: {
		fontSize: '13px',
		fontStyle: 'normal',
		fontWeight: 700,
		color: theme.palette.primary.main,
	},
	dateInfo: {
		fontSize: '12px',
		fontStyle: 'normal',
		fontWeight: 700,
		color: theme.palette.secondary.main,
	},
	logo: {
		height: '22px',
		maxWidth: '54px',
		objectFit: 'contain',
	},

	airportCode: airportCss,
	hour: hourCss,
	hourAlightRight: { ...hourCss, float: 'right' },
	airportCodeAlightRight: { ...airportCss, float: 'right' },
	separator: {
		width: '100%',
		marginTop: '16px',
		background: 'linear-gradient(90deg, #31194B 0%, #ED6602 100%)',
		borderRadius: '999px',
		height: '2px',
		position: 'relative',
		'&:before': {
			content: '""',
			position: 'absolute',
			left: 0,
			top: -4,
			width: 10,
			height: 10,
			borderRadius: '50%',
			backgroundColor: '#31194B',
		},
		'&:after': {
			content: '""',
			position: 'absolute',
			right: 0,
			top: -4,
			width: 10,
			height: 10,
			borderRadius: '50%',
			backgroundColor: '#ED6602',
		},
	},
	separatorContainer: {
		textAlign: 'center',
	},
	rowReverse: {
		flexDirection: 'row-reverse',
	},
	days: {
		fontSize: '11px',
		fontStyle: 'normal',
		fontWeight: 700,
		color: '#ED6602',
	},
}));
interface Props {
	segment: QuotationFlightInfo;
	segmentIndex: number;
	totalSegments: number;
	compact?: boolean;
}

export const FlightSegmentMobile = (props: Props): JSX.Element => {
	const classes = useStyles();
	const { segment, segmentIndex, totalSegments, compact = false } = props;
	const departureDate = moment(segment.departure.date, 'YYYY-MM-DD');
	const arrivalDate = moment(segment.arrival.date, 'YYYY-MM-DD');
	const dayDifference: number = arrivalDate.diff(departureDate, 'days');

	if (compact) {
		return (
			<Box
				sx={{
					display: 'grid',
					gridTemplateColumns: '64px 22px minmax(0, 1fr)',
					columnGap: 1,
					py: 1.25,
					borderBottom: segmentIndex + 1 === totalSegments ? 'none' : '1px solid rgba(49, 25, 75, 0.08)',
				}}
			>
				<Box textAlign="right">
					<Typography variant="body2" fontWeight={800} color="text.primary" lineHeight={1.1}>
						{segment.departure.hour.substring(0, 5)}
					</Typography>
					<Typography variant="caption" color="text.secondary" fontWeight={600} lineHeight={1.15} display="block">
						{isoDateToFullDate(segment.departure.date)}
					</Typography>
					<Box height={28} display="flex" alignItems="center" justifyContent="flex-end">
						<Typography variant="caption" color="text.secondary" fontWeight={700}>
							{segment.flightNumber}
						</Typography>
					</Box>
					<Typography variant="body2" fontWeight={800} color="text.primary" lineHeight={1.1}>
						{segment.arrival.hour.substring(0, 5)}
					</Typography>
					<Typography variant="caption" color="text.secondary" fontWeight={600} lineHeight={1.15} display="block">
						{isoDateToFullDate(segment.arrival.date)}
					</Typography>
				</Box>

				<Box display="flex" flexDirection="column" alignItems="center" pt={0.5}>
					<Box width={7} height={7} borderRadius="50%" bgcolor="rgba(49, 25, 75, 0.70)" />
					<Box width="1px" flex={1} minHeight={34} bgcolor="rgba(49, 25, 75, 0.18)" my={0.5} />
					<Box
						width={20}
						height={20}
						borderRadius="50%"
						border="1px solid rgba(49, 25, 75, 0.14)"
						bgcolor="#fff"
						display="flex"
						alignItems="center"
						justifyContent="center"
					>
						<FlightIcon sx={{ fontSize: 12, color: 'secondary.main', transform: 'rotate(90deg)' }} />
					</Box>
					<Box width="1px" flex={1} minHeight={34} bgcolor="rgba(49, 25, 75, 0.18)" my={0.5} />
					<Box width={7} height={7} borderRadius="50%" bgcolor="rgba(49, 25, 75, 0.70)" />
				</Box>

				<Box minWidth={0}>
					<Box display="flex" justifyContent="space-between" gap={1} alignItems="flex-start">
						<Box minWidth={0}>
							<Typography variant="body2" fontWeight={800} color="primary" lineHeight={1.2}>
								{segment.departure.airport.cityName} · {segment.departure.airport.code}
							</Typography>
							<Typography variant="caption" color="text.secondary" lineHeight={1.2} display="block">
								{segment.departure.airport.name}
							</Typography>
						</Box>
						<Chip
							size="small"
							label={`Leg ${segmentIndex + 1}`}
							sx={{
								height: 20,
								borderRadius: '999px',
								fontSize: 10,
								fontWeight: 800,
								color: 'secondary.main',
								bgcolor: 'rgba(237, 102, 2, 0.08)',
							}}
						/>
					</Box>

					<Box my={1.15}>
						<Box
							display="inline-flex"
							alignItems="center"
							gap={0.75}
							px={1}
							py={0.35}
							borderRadius="999px"
							bgcolor="rgba(49, 25, 75, 0.045)"
						>
							<img
								src={segment.airline.image}
								alt={segment.airline.name}
								style={{ height: 14, maxWidth: 34, objectFit: 'contain' }}
							/>
							<Typography variant="caption" color="text.primary" fontWeight={700} lineHeight={1.2}>
								{segment.airline.name}
							</Typography>
						</Box>
					</Box>

					<Box display="flex" justifyContent="space-between" gap={1} alignItems="flex-start">
						<Box minWidth={0}>
							<Typography variant="body2" fontWeight={800} color="primary" lineHeight={1.2}>
								{segment.arrival.airport.cityName} · {segment.arrival.airport.code}
							</Typography>
							<Typography variant="caption" color="text.secondary" lineHeight={1.2} display="block">
								{segment.arrival.airport.name}
							</Typography>
						</Box>
						{dayDifference > 0 && (
							<Typography variant="caption" color="secondary" fontWeight={800} whiteSpace="nowrap">
								+{dayDifference} día
							</Typography>
						)}
					</Box>
				</Box>
			</Box>
		);
	}

	return (
		<Card
			className={classes.card}
			sx={
				compact
					? {
							mt: 0.75,
							borderRadius: '8px',
							borderColor: 'rgba(49, 25, 75, 0.08)',
							boxShadow: 'none',
							backgroundColor: 'rgba(255, 255, 255, 0.82)',
					  }
					: undefined
			}
		>
			<CardContent sx={{ p: compact ? 1.25 : 1.75, '&:last-child': { pb: compact ? 1.25 : 1.75 } }}>
				<Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={compact ? 1 : 1.5}>
					<Box display="flex" alignItems="center" gap={1} minWidth={0}>
						<img
							src={segment.airline.image}
							alt={segment.airline.name}
							className={classes.logo}
							style={compact ? { height: 18, maxWidth: 44 } : undefined}
						/>
						<Box minWidth={0}>
							<Typography
								className={classes.airlineName}
								component={'p'}
								variant={'body2'}
								noWrap
								sx={compact ? { fontSize: 12, color: 'text.primary' } : undefined}
							>
								{segment.airline.name}
							</Typography>
							<Typography component={'p'} variant={'caption'} color="text.secondary" fontWeight={600} lineHeight={1.1}>
								{segment.flightNumber}
							</Typography>
						</Box>
					</Box>
					<Chip
						size="small"
						label={
							totalSegments > 1
								? `Tramo ${segmentIndex + 1} de ${totalSegments}`
								: isoDateToFullDate(segment.departure.date)
						}
						className={classes.dateInfo}
						sx={{
							backgroundColor: compact ? 'rgba(49, 25, 75, 0.06)' : 'rgba(237, 102, 2, 0.10)',
							color: compact ? 'text.secondary' : 'secondary.main',
							borderRadius: '999px',
							height: compact ? 22 : undefined,
							fontSize: compact ? 11 : undefined,
						}}
					/>
				</Box>
				{totalSegments > 1 && (
					<Typography component="p" variant="caption" color="text.secondary" fontWeight={600} mb={compact ? 0.75 : 1}>
						{isoDateToFullDate(segment.departure.date)}
					</Typography>
				)}

				<Grid container alignItems="center" spacing={1}>
					<Grid item xs={3}>
						<Typography
							className={classes.airportCode}
							component={'p'}
							variant={'body2'}
							sx={compact ? { fontSize: 17 } : undefined}
						>
							{segment.departure.airport.code}
						</Typography>
						<Typography
							className={classes.hour}
							component={'p'}
							variant={'body2'}
							sx={compact ? { fontSize: 14 } : undefined}
						>
							{segment.departure.hour.substring(0, 5)}
						</Typography>
					</Grid>
					<Grid item xs={6} className={classes.separatorContainer}>
						<Box
							className={classes.separator}
							sx={
								compact
									? {
											mt: 1.25,
											height: 1,
											background: 'rgba(49, 25, 75, 0.22)',
											'&:before, &:after': {
												top: -3,
												width: 7,
												height: 7,
												backgroundColor: 'rgba(49, 25, 75, 0.38)',
											},
									  }
									: undefined
							}
						/>
					</Grid>
					<Grid item xs={3} textAlign="right">
						<Typography
							className={classes.airportCode}
							component={'p'}
							variant={'body2'}
							sx={compact ? { fontSize: 17 } : undefined}
						>
							{segment.arrival.airport.code}
						</Typography>
						<Box display="flex" justifyContent="flex-end" alignItems="baseline" gap={0.5}>
							{dayDifference > 0 && (
								<Typography className={classes.days} component={'span'} variant={'caption'}>
									+{dayDifference} día
								</Typography>
							)}
							<Typography
								className={classes.hour}
								component={'span'}
								variant={'body2'}
								sx={compact ? { fontSize: 14 } : undefined}
							>
								{segment.arrival.hour.substring(0, 5)}
							</Typography>
						</Box>
					</Grid>
				</Grid>
				<Grid container mt={compact ? 0.5 : 0.75}>
					<Grid item xs={5}>
						<Typography
							className={classes.airportCode}
							component={'p'}
							variant={'body2'}
							fontSize={compact ? 11 : 12}
							fontWeight={compact ? 500 : 600}
							color={compact ? 'text.primary' : undefined}
						>
							{segment.departure.airport.cityName}
						</Typography>
						<Typography component={'p'} variant={'caption'} color="text.secondary" lineHeight={1.15}>
							{segment.departure.airport.name}
						</Typography>
					</Grid>
					<Grid item xs={2}></Grid>
					<Grid item xs={5} textAlign="right">
						<Typography
							className={classes.airportCode}
							component={'p'}
							variant={'body2'}
							fontSize={compact ? 11 : 12}
							fontWeight={compact ? 500 : 600}
							color={compact ? 'text.primary' : undefined}
						>
							{segment.arrival.airport.cityName}
						</Typography>
						<Typography component={'p'} variant={'caption'} color="text.secondary" lineHeight={1.15}>
							{segment.arrival.airport.name}
						</Typography>
					</Grid>
				</Grid>
			</CardContent>
		</Card>
	);
};
